import {clone} from "lodash-es";
import useStorage from "./useStorage";

/** Local storage key — kept as-is so existing baskets survive the redesign. */
export const BASKET_STORAGE_KEY = 'Koszyk-pelen-keszu'

export type BasketItems = Record<string, { count: number }>

export type BasketState = {
    items: BasketItems
    error: boolean
    sent: boolean
}

export const defaultBasketState: BasketState = {
    items: {},
    error: false,
    sent: false
}

/**
 * Single owner of the enquiry basket. Every surface (card, product page,
 * drawer, header bubble) reads and mutates it through this hook so the counters
 * can never drift apart.
 */
const useBasket = () => {

    const [basketState, setBasketState] = useStorage<BasketState>('localStorage', BASKET_STORAGE_KEY, defaultBasketState)

    const items = basketState?.items ?? {}

    const totalCount = Object.keys(items).reduce((accumulator, key) => accumulator + (items[key]?.count ?? 0), 0)

    const getCount = (id: string) => items[id]?.count ?? 0

    /** Adds one unit, never going over the available stock. */
    const addItem = (id: string, available?: number) => {
        setBasketState((state: BasketState) => {
            const current = state.items?.[id]?.count ?? 0
            const next = current + 1
            if (available !== undefined && next > available) {
                return state
            }
            return {
                ...state,
                sent: false,
                items: {...state.items, [id]: {count: next}}
            }
        })
    }

    /** Removes one unit and drops the line once it hits zero. */
    const removeItem = (id: string) => {
        setBasketState((state: BasketState) => {
            const next = (state.items?.[id]?.count ?? 0) - 1
            if (next > 0) {
                return {
                    ...state,
                    items: {...state.items, [id]: {count: next}}
                }
            }
            const items = clone(state.items ?? {})
            delete items[id]
            return {...state, items}
        })
    }

    const dropItem = (id: string) => {
        setBasketState((state: BasketState) => {
            const items = clone(state.items ?? {})
            delete items[id]
            return {...state, items}
        })
    }

    const reset = () => setBasketState(defaultBasketState)

    return ({
        basketState,
        setBasketState,
        items,
        totalCount,
        getCount,
        addItem,
        removeItem,
        dropItem,
        reset
    })
}

export default useBasket
